import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Calendar, Star, Users, Clock } from "lucide-react";

const JuniorPrograms = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const camps = [
    { week: "Week 1", dates: "July 13 – July 17" },
    { week: "Week 2", dates: "July 27 – July 31" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[450px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(/images/junior1b.jpg)" }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Next Generation</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">2026 Junior Camps & Leagues</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            Inspiring a love of the game in young golfers through structured, fun, and professional instruction.
          </p>
        </div>
      </section>

      {/* Junior Golf Camps */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Summer Camps</span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">2026 Junior Golf Camps</h2>
              <p className="text-gray-500 mb-2 text-sm font-medium">Week-long camps · Ages 5 to 13 · Mon–Fri, 9am–11am</p>
              <p className="text-gray-600 leading-relaxed mb-6">
                This camp is structured to give junior golfers the basic swing, stance, and full range of fundamentals to continue growing their interest and comfort level in the game of golf.
              </p>

              {/* Camp Weeks */}
              <div className="flex gap-4 mb-6">
                {camps.map((c) => (
                  <div key={c.week} className="flex-1 bg-[#f8f7f4] rounded-xl p-4 text-center border border-gray-100">
                    <div className="text-xs font-bold text-[#C9A96E] uppercase tracking-wide mb-1">{c.week}</div>
                    <div className="text-sm font-semibold text-[#1B2A4A]">{c.dates}</div>
                  </div>
                ))}
              </div>

              {/* Cost */}
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#1B2A4A] text-white px-5 py-2 rounded-lg font-bold text-lg">$150 per camp</div>
                <span className="text-gray-400 text-sm">HST included</span>
              </div>

              {/* What's included */}
              <h3 className="text-sm font-semibold text-[#1B2A4A] mb-3 uppercase tracking-wide">What's Included</h3>
              <ul className="space-y-2">
                {[
                  "10 hours of professional instruction",
                  "Snack with beverage every day",
                  "Lunch party Friday at 10:30am",
                  "All fundamentals: swing, stance & more",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <Check size={14} className="text-[#C9A96E] mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="/images/junior3.jpg"
                alt="Junior golf camp"
                className="w-full h-[460px] object-cover"
              />
            </div>
          </div>

          <hr className="border-gray-100 mb-20" />

          {/* Junior League */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
              <img
                src="/images/junior1.jpg"
                alt="Junior league"
                className="rounded-2xl h-52 object-cover w-full"
              />
              <img
                src="/images/junior4.jpg"
                alt="Junior league"
                className="rounded-2xl h-52 object-cover w-full mt-8"
              />
              <img
                src="/images/junior3.jpg"
                alt="Junior program"
                className="rounded-2xl h-52 object-cover w-full -mt-8"
              />
              <img
                src="/images/junior1b.jpg"
                alt="Junior program"
                className="rounded-2xl h-52 object-cover w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Summer League</span>
              <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">2026 Junior League</h2>
              <p className="text-gray-500 mb-2 text-sm font-medium">Monday Afternoons · Ages 9 to 16 · Tee off 3:00 PM</p>
              <p className="text-gray-600 leading-relaxed mb-2">
                Every Monday from <strong>July 6th to August 31st</strong>. <em>(No league on Monday August 3rd.)</em>
              </p>
              <p className="text-gray-500 text-sm mb-6">Ability to play 9 holes of golf with other juniors required.</p>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#1B2A4A] rounded-xl p-4 text-center">
                  <div className="text-xs text-white/60 uppercase tracking-wide mb-1">Members</div>
                  <div className="text-2xl font-bold text-white">$150</div>
                </div>
                <div className="bg-[#f8f7f4] rounded-xl p-4 text-center border border-gray-100">
                  <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Non-Members</div>
                  <div className="text-2xl font-bold text-[#1B2A4A]">$250</div>
                </div>
              </div>

              {/* Value breakdown */}
              <div className="bg-[#C9A96E]/10 border border-[#C9A96E]/30 rounded-xl p-5 mb-6">
                <div className="text-sm font-semibold text-[#1B2A4A] mb-3">Total Value Breakdown</div>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex justify-between"><span>8 weeks of league golf (9-hole)</span><span className="font-medium">$240</span></li>
                  <li className="flex justify-between"><span>Unlimited range balls & practice facility (all summer)</span><span className="font-medium">$400</span></li>
                  <li className="flex justify-between"><span>Year-end BBQ (August 26th at 2pm)</span><span className="font-medium">$20</span></li>
                  <li className="flex justify-between border-t border-[#C9A96E]/30 pt-2 mt-1 font-bold text-[#1B2A4A]"><span>Total Value</span><span>$660</span></li>
                </ul>
              </div>

              {/* Year-end BBQ note */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                <Calendar size={14} className="text-[#C9A96E] shrink-0" />
                <span>Year-end BBQ: <strong>August 26th at 2:00 PM</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Junior Membership Prices */}
      <section className="py-16 bg-[#f8f7f4]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Membership</span>
          <h2 className="text-3xl font-serif font-bold text-[#1B2A4A] mt-3 mb-2">Junior Membership Prices</h2>
          <p className="text-gray-400 text-sm mb-8">All prices are HST included</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { label: "12 and under", price: "$250" },
              { label: "13 to 16 years", price: "$350" },
              { label: "17 and 18 years", price: "$450" },
            ].map((t) => (
              <div key={t.label} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="text-sm text-gray-400 mb-2">{t.label}</div>
                <div className="text-3xl font-bold text-[#1B2A4A]">{t.price}</div>
                <div className="text-xs text-gray-400 mt-1">HST included</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-6 italic">
            See our <Link to="/memberships" className="text-[#C9A96E] hover:underline">Memberships page</Link> for full junior membership pricing including HST-exclusive rates.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Register Your Junior Golfer
          </h2>
          <p className="text-white/60 mb-8">
            Spots fill quickly — contact us to register your child for a camp or league this summer.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors"
            >
              Register Now <ArrowRight size={18} />
            </Link>
            <a
              href="tel:5196768772"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors"
            >
              Call (519) 676-8772
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JuniorPrograms;
